/**
 * SimulationContext — Production-ready "View As" role simulation.
 *
 * DRR-256: Role Simulation for RBAC Debugging
 *
 * Allows architects/admins to preview what the UI looks like for different roles
 * WITHOUT actually changing their authentication. This is READ-ONLY simulation —
 * write operations still use real auth.
 *
 * Key differences from DevImpersonationPanel:
 * - Works in production (not dev-only)
 * - Doesn't change actual auth headers
 * - Audit trail when simulation is active
 * - Authorization check (architect can simulate any, admin only below their level)
 *
 * Usage:
 *   const { startSimulation, stopSimulation, isSimulating, simulatedScopes } = useSimulation();
 *
 *   // Start viewing as a ULP
 *   startSimulation({ roleId: '144g-ulp-basic', roleName: 'ULP', scopes: [...] });
 *
 *   // Check if simulating
 *   if (isSimulating) { ... }
 *
 *   // Components use useEffectiveScopes() which returns simulated OR real scopes
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth, useViewLayer } from './AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SimulatedRole {
  roleId: string;
  roleName: string;
  scopes: string[];
  viewLayer: 'user' | 'admin' | 'architect';
}

interface SimulationState {
  /** Whether simulation mode is active */
  isSimulating: boolean;
  /** The role being simulated (null if not simulating) */
  simulatedRole: SimulatedRole | null;
  /** Timestamp when simulation started (for audit) */
  simulationStartedAt: Date | null;
}

interface SimulationActions {
  /** Start simulating a role. Returns false if not authorized. */
  startSimulation: (role: SimulatedRole) => boolean;
  /** Stop simulation and return to real auth */
  stopSimulation: () => void;
  /** Check if current user can simulate a given role */
  canSimulateRole: (targetViewLayer: 'user' | 'admin' | 'architect') => boolean;
}

type SimulationContextType = SimulationState & SimulationActions;

// ── Context ───────────────────────────────────────────────────────────────────

const SimulationContext = createContext<SimulationContextType | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function SimulationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const realViewLayer = useViewLayer();

  const [state, setState] = useState<SimulationState>({
    isSimulating: false,
    simulatedRole: null,
    simulationStartedAt: null,
  });

  /**
   * Authorization rules for simulation:
   * - architect: can simulate any role (user, admin, architect)
   * - admin: can simulate user roles only
   * - user: cannot simulate (no access to simulation UI anyway)
   */
  const canSimulateRole = useCallback(
    (targetViewLayer: 'user' | 'admin' | 'architect'): boolean => {
      if (realViewLayer === 'architect') {
        return true; // Architect can simulate anything
      }
      if (realViewLayer === 'admin') {
        return targetViewLayer === 'user'; // Admin can only simulate user
      }
      return false; // Users cannot simulate
    },
    [realViewLayer]
  );

  const startSimulation = useCallback(
    (role: SimulatedRole): boolean => {
      // Authorization check
      if (!canSimulateRole(role.viewLayer)) {
        console.warn(
          `[Simulation] Denied: ${realViewLayer} cannot simulate ${role.viewLayer}`
        );
        return false;
      }

      // Audit log
      console.info(
        `[Simulation] Started: ${user?.email ?? 'unknown'} viewing as ${role.roleName} (${role.roleId})`
      );

      setState({
        isSimulating: true,
        simulatedRole: role,
        simulationStartedAt: new Date(),
      });

      return true;
    },
    [canSimulateRole, realViewLayer, user?.email]
  );

  const stopSimulation = useCallback(() => {
    if (state.isSimulating) {
      const duration = state.simulationStartedAt
        ? Math.round((Date.now() - state.simulationStartedAt.getTime()) / 1000)
        : 0;
      console.info(
        `[Simulation] Ended: ${user?.email ?? 'unknown'} simulated ${state.simulatedRole?.roleName} for ${duration}s`
      );
    }

    setState({
      isSimulating: false,
      simulatedRole: null,
      simulationStartedAt: null,
    });
  }, [state.isSimulating, state.simulatedRole, state.simulationStartedAt, user?.email]);

  const value = useMemo(
    (): SimulationContextType => ({
      ...state,
      startSimulation,
      stopSimulation,
      canSimulateRole,
    }),
    [state, startSimulation, stopSimulation, canSimulateRole]
  );

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Access simulation state and controls.
 */
export function useSimulation(): SimulationContextType {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return context;
}

/**
 * Get effective scopes — simulated if simulating, real otherwise.
 *
 * Use this in components that need to check permissions for UI rendering.
 * Backend still uses real auth for actual operations.
 */
export function useEffectiveScopes(): string[] {
  const context = useContext(SimulationContext);
  const { user } = useAuth();

  // If no simulation context (provider not mounted), use real scopes
  if (!context) {
    // Fallback: try to get scopes from localStorage (dev mode)
    try {
      const stored = localStorage.getItem('dev_actor_scopes');
      return stored ? stored.split(',') : [];
    } catch {
      return [];
    }
  }

  // If simulating, return simulated scopes
  if (context.isSimulating && context.simulatedRole) {
    return context.simulatedRole.scopes;
  }

  // Otherwise, get real scopes from localStorage (set by auth flow)
  try {
    const stored = localStorage.getItem('dev_actor_scopes');
    return stored ? stored.split(',') : [];
  } catch {
    return [];
  }
}

/**
 * Get effective view layer — simulated if simulating, real otherwise.
 */
export function useEffectiveViewLayer(): 'user' | 'admin' | 'architect' {
  const context = useContext(SimulationContext);
  const realViewLayer = useViewLayer();

  if (context?.isSimulating && context.simulatedRole) {
    return context.simulatedRole.viewLayer;
  }

  return realViewLayer;
}

/**
 * Check if user has a scope (considering simulation).
 *
 * Supports wildcard patterns: *, *:*, ns:*
 */
export function useHasScope(scope: string): boolean {
  const scopes = useEffectiveScopes();
  return checkScopeMatch(scopes, scope);
}

/**
 * Check if user has ANY of the given scopes (considering simulation).
 */
export function useHasScopeAny(needed: string[]): boolean {
  const scopes = useEffectiveScopes();
  return needed.some((n) => checkScopeMatch(scopes, n));
}

/**
 * Check if user has ALL of the given scopes (considering simulation).
 */
export function useHasScopeAll(needed: string[]): boolean {
  const scopes = useEffectiveScopes();
  return needed.every((n) => checkScopeMatch(scopes, n));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Check if a scope matches any granted scope pattern.
 * Mirrors AuthCtx::matches_scope from Rust backend.
 */
function checkScopeMatch(granted: string[], needed: string): boolean {
  // Wildcard grants all
  if (granted.includes('*') || granted.includes('*:*')) {
    return true;
  }

  // Exact match
  if (granted.includes(needed)) {
    return true;
  }

  // Namespace wildcard: task:* matches task:read
  const parts = needed.split(':');
  if (parts.length >= 2) {
    const nsWild = `${parts[0]}:*`;
    if (granted.includes(nsWild)) {
      return true;
    }
  }

  // 3-part namespace wildcard: fhir:*:* matches fhir:patient:read
  if (parts.length >= 3) {
    const nsWild3 = `${parts[0]}:*:*`;
    if (granted.includes(nsWild3)) {
      return true;
    }
  }

  return false;
}
